import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Typography,
  Autocomplete, TextField,
} from '@mui/material';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getPurchaseOrders, getPurchaseOrder, getPurchaseOrderItems } from '../../api/procurement';
import { getGrns, getGrn, getGrnItems } from '../../api/grn';
import { createPurchaseInvoice } from '../../api/purchaseInvoice';
import { getVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import { generateTxnNumber, formatQty, formatCurrency, parsePositiveRate, parseWholeQty, sanitizeQtyInput } from '../../utils';
import {
  buildCreatePurchaseInvoiceBody,
  computeInvoiceFinancials,
  findPoItemForGrnLine,
  formatCreateInvoiceError,
  invoiceLineTotal,
  mapGrnItemToInvoiceLine,
  normalizeDiscountPercent,
  normalizeTaxPercent,
  validateCreateInvoice,
} from '../../utils/purchaseInvoiceEntity';
import { PAYMENT_TERMS_OPTIONS, NET_DAYS_BY_PAYMENT_TERM } from '../../constants';
import { IPD_COLORS, inputSx } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function PurchaseInvoiceForm() {
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const base = url.replace(/\/purchase-invoice\/new$/, '');
  const { resolveItem } = useItemNames();
  const params = new URLSearchParams(location.search);
  const preselectedGrnId = params.get('grn_id');
  const preselectedPoId = params.get('po_id');

  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posted, setPosted] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState(null);

  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState(preselectedPoId || '');
  const [po, setPo] = useState(null);
  const [poItems, setPoItems] = useState([]);

  const [grnsForPo, setGrnsForPo] = useState([]);
  const [grnsLoading, setGrnsLoading] = useState(false);
  const [selectedGrnId, setSelectedGrnId] = useState(preselectedGrnId || '');
  const [grn, setGrn] = useState(null);

  const [invoiceNumber] = useState(() => generateTxnNumber('SUP-INV'));
  const [invoiceDate, setInvoiceDate] = useState(todayIso);
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [dueDate, setDueDate] = useState(() => addDaysIso(todayIso(), 30));
  const [lines, setLines] = useState([]);

  // Lookups: all POs (searchable) + vendors, loaded once.
  useEffect(() => {
    setLookupsLoading(true);
    Promise.all([
      getPurchaseOrders({}).catch(() => []),
      getVendors().catch(() => []),
    ]).then(([poData, vendorData]) => {
      setPos(normalizeList(poData));
      setVendors(normalizeList(vendorData));
    }).finally(() => setLookupsLoading(false));
  }, []);

  // Deep-linked from a GRN's "Create Purchase Invoice" action — resolve its PO first.
  useEffect(() => {
    if (!preselectedGrnId || preselectedPoId) return;
    getGrn(preselectedGrnId).then((g) => {
      if (g?.purchase_order_id) setSelectedPoId(g.purchase_order_id);
    }).catch(() => {});
  }, [preselectedGrnId, preselectedPoId]);

  // PO selected -> load PO detail/items + GRNs already posted to stock against it.
  useEffect(() => {
    if (!selectedPoId) {
      setPo(null); setPoItems([]); setGrnsForPo([]);
      return;
    }
    let alive = true;
    setGrnsLoading(true);
    getPurchaseOrder(selectedPoId).then(async (p) => {
      if (!alive) return;
      setPo(p);
      const scope = { tenant_id: p.tenant_id, hospital_id: p.hospital_id };
      const [itemsData, grnData] = await Promise.all([
        getPurchaseOrderItems(selectedPoId, scope).catch(() => []),
        getGrns({ purchase_order_id: selectedPoId }).catch(() => []),
      ]);
      if (!alive) return;
      setPoItems(normalizeList(itemsData));
      setGrnsForPo(normalizeList(grnData).filter(g => (g.posting_status || g.status) === 'POSTED_TO_STOCK'));
    }).catch((e) => {
      toast.error(e.message || 'Failed to load purchase order');
      setPo(null); setPoItems([]); setGrnsForPo([]);
    }).finally(() => { if (alive) setGrnsLoading(false); });
    return () => { alive = false; };
  }, [selectedPoId]);

  // GRN selected (and PO items available) -> build the editable invoice line table.
  useEffect(() => {
    if (!selectedGrnId) { setGrn(null); setLines([]); return; }
    let alive = true;
    setLinesLoading(true);
    getGrn(selectedGrnId).then(async (g) => {
      if (!alive) return;
      setGrn(g);
      const scope = { tenant_id: g.tenant_id, hospital_id: g.hospital_id };
      const grnItemsData = await getGrnItems(selectedGrnId, scope).catch(() => []);
      const builtLines = normalizeList(grnItemsData)
        .map((gi) => {
          const qty = Math.floor(Number(gi.accepted_quantity ?? gi.received_quantity) || 0);
          if (qty <= 0) return null;
          const poItem = findPoItemForGrnLine(gi, poItems);
          return mapGrnItemToInvoiceLine(gi, poItem, resolveItem(gi));
        })
        .filter(Boolean);
      setLines(builtLines);
      if (!builtLines.length) toast.info('This GRN has no receivable quantity to invoice.');
    }).catch((e) => {
      toast.error(e.message || 'Failed to load GRN');
      setGrn(null); setLines([]);
    }).finally(() => { if (alive) setLinesLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrnId, poItems]);

  function resolveVendorName(vendorId) {
    if (!vendorId) return '—';
    const v = vendors.find(x => (x.vendor_id || x.id) === vendorId);
    if (!v) return vendorId;
    return v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name;
  }

  function updateLine(index, key, value) {
    setLines(prev => prev.map((line, i) => {
      if (i !== index) return line;
      if (key === 'invoiced_quantity') {
        return { ...line, [key]: sanitizeQtyInput(value) };
      }
      if (key === 'discount_percent') {
        return { ...line, [key]: String(normalizeDiscountPercent(value)) };
      }
      if (key === 'tax_percent') {
        return { ...line, [key]: String(normalizeTaxPercent(value)) };
      }
      return { ...line, [key]: value };
    }));
  }

  function handleInvoiceDateChange(value) {
    setInvoiceDate(value);
    const days = NET_DAYS_BY_PAYMENT_TERM[paymentTerms];
    if (days != null) setDueDate(addDaysIso(value, days));
  }

  function handlePaymentTermsChange(value) {
    setPaymentTerms(value);
    const days = NET_DAYS_BY_PAYMENT_TERM[value];
    if (days != null) setDueDate(addDaysIso(invoiceDate, days));
  }

  const financials = useMemo(() => computeInvoiceFinancials(lines), [lines]);
  const validationError = useMemo(() => validateCreateInvoice({
    po,
    grn,
    invoiceNumber,
    invoiceDate,
    dueDate,
    paymentTerms,
    lines,
  }), [po, grn, invoiceNumber, invoiceDate, dueDate, paymentTerms, lines]);

  async function handleSubmit() {
    const err = validateCreateInvoice({
      po, grn, invoiceNumber, invoiceDate, dueDate, paymentTerms, lines,
    });
    if (err) { toast.error(err); return; }

    setSaving(true);
    try {
      const body = buildCreatePurchaseInvoiceBody({
        invoiceNumber,
        po,
        grn,
        selectedPoId,
        selectedGrnId,
        invoiceDate,
        dueDate,
        paymentTerms,
        lines,
      });
      const created = await createPurchaseInvoice(body);
      toast.success('Purchase invoice created');
      setCreatedInvoice(created);
      setPosted(true);
    } catch (e) {
      toast.error(formatCreateInvoiceError(e));
    } finally {
      setSaving(false);
    }
  }

  if (posted) {
    const newId = createdInvoice?.purchase_invoice_id || createdInvoice?.id;
    return (
      <Box>
        <SectionCard title="">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, mb: 1 }}>
              Purchase Invoice Created
            </Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 3 }}>
              {invoiceNumber} has been recorded as a <strong>DRAFT</strong> against GRN {grn?.grn_number}.
              The next step is to run the <strong>3-way match</strong> to verify quantities and rates
              before posting to finance.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <InvButton variant="outlined" tone="teal" onClick={() => history.push(`${base}/purchase-invoices`)}>
                View All Invoices
              </InvButton>
              {newId && (
                <InvButton tone="mint" onClick={() => history.push(`${base}/purchase-invoice/${newId}`)}>
                  Go to Invoice → Run Match
                </InvButton>
              )}
            </Box>
          </Box>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Purchase Invoices"
        title="Create Purchase Invoice"
        actions={<InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>}
      />

      {!preselectedGrnId && (
        <SectionCard title="Source: Purchase Order & GRN" sx={{ mb: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 0.5 }}>Purchase Order</Typography>
              <Autocomplete
                size="small"
                loading={lookupsLoading}
                options={pos}
                value={pos.find(p => (p.purchase_order_id || p.id) === selectedPoId) || null}
                getOptionLabel={(p) => (p ? `${p.po_number || p.purchase_order_id} — ${resolveVendorName(p.vendor_id)}` : '')}
                isOptionEqualToValue={(a, b) => (a?.purchase_order_id || a?.id) === (b?.purchase_order_id || b?.id)}
                onChange={(_, val) => {
                  setSelectedPoId(val ? (val.purchase_order_id || val.id) : '');
                  setSelectedGrnId('');
                }}
                renderInput={(paramsIn) => (
                  <TextField {...paramsIn} placeholder="Search purchase order by number or vendor…" sx={inputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InvSelect
                label="Posted GRN"
                value={selectedGrnId}
                onChange={e => setSelectedGrnId(e.target.value)}
                disabled={!selectedPoId || grnsLoading}
                options={grnsForPo.map(g => ({ value: g.grn_id || g.id, label: g.grn_number }))}
                helperText={
                  !selectedPoId ? 'Select a purchase order first'
                    : (!grnsLoading && !grnsForPo.length ? 'No GRNs posted to stock for this PO yet' : undefined)
                }
              />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {(linesLoading || (grnsLoading && preselectedGrnId)) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: IPD_COLORS.primary }} />
        </Box>
      )}

      {!linesLoading && grn && (
        <>
          <SectionCard title="Invoice Details" sx={{ mb: 2.5 }}>
            <Grid container spacing={2} sx={{ maxWidth: 960 }}>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Invoice Number" value={invoiceNumber} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Vendor" value={resolveVendorName(po?.vendor_id)} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Purchase Order" value={po?.po_number || selectedPoId} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Source GRN" value={grn.grn_number} disabled />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Invoice Date" type="date" value={invoiceDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={e => handleInvoiceDateChange(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvSelect label="Payment Terms" value={paymentTerms}
                  onChange={e => handlePaymentTermsChange(e.target.value)} options={PAYMENT_TERMS_OPTIONS} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InvTextField label="Due Date" type="date" value={dueDate}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(dueDate && invoiceDate && dueDate < invoiceDate)}
                  helperText={dueDate && invoiceDate && dueDate < invoiceDate ? 'Cannot be before invoice date' : undefined}
                  onChange={e => setDueDate(e.target.value)} />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Invoice Line Items">
            {!lines.length ? (
              <Typography sx={{ color: IPD_COLORS.textSecondary, py: 2 }}>
                No receivable quantity found on this GRN.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 900, '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>GRN Qty</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 120 }}>Invoiced Qty</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 130 }}>Unit Rate</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Disc %</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line, i) => {
                      const qty = parseWholeQty(line.invoiced_quantity);
                      const exceeds = qty != null && qty > line.grnQty;
                      const qtyInvalid = !qty || exceeds;
                      const rateInvalid = parsePositiveRate(line.unit_rate) == null;
                      return (
                        <TableRow key={line.grn_item_id || i}>
                          <TableCell>{line.item_name}</TableCell>
                          <TableCell align="right">{formatQty(line.grnQty)}</TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <InvTextField
                              value={line.invoiced_quantity}
                              label=""
                              type="number"
                              inputProps={{ min: 1, max: line.grnQty, step: 1 }}
                              error={qtyInvalid}
                              helperText={exceeds ? `Max ${line.grnQty}` : (!qty && line.invoiced_quantity !== '' ? 'Whole number required' : undefined)}
                              onChange={e => updateLine(i, 'invoiced_quantity', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <InvTextField
                              value={line.unit_rate}
                              label=""
                              type="number"
                              inputProps={{ min: 0.01, step: '0.01' }}
                              error={rateInvalid}
                              helperText={rateInvalid && line.unit_rate !== '' ? 'Must be greater than 0' : undefined}
                              onChange={e => updateLine(i, 'unit_rate', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <InvTextField
                              value={line.discount_percent}
                              label=""
                              type="number"
                              inputProps={{ min: 0, max: 100, step: '0.01' }}
                              onChange={e => updateLine(i, 'discount_percent', e.target.value)}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1 }}>
                            <InvTextField
                              value={line.tax_percent}
                              label=""
                              type="number"
                              inputProps={{ min: 0, max: 100, step: '0.01' }}
                              onChange={e => updateLine(i, 'tax_percent', e.target.value)}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(invoiceLineTotal(line))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {lines.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ minWidth: 280 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Subtotal</Typography>
                    <Typography sx={{ fontSize: 13, fontFamily: 'monospace' }}>{formatCurrency(financials.subtotal)}</Typography>
                  </Box>
                  {financials.totalDiscount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Discount</Typography>
                      <Typography sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                        {formatCurrency(financials.totalDiscount)}
                      </Typography>
                    </Box>
                  )}
                  {financials.totalTax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Tax</Typography>
                      <Typography sx={{ fontSize: 13, fontFamily: 'monospace' }}>
                        {formatCurrency(financials.totalTax)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between', py: 1, mt: 0.5,
                    borderTop: `2px solid ${IPD_COLORS.border}`,
                  }}>
                    <Typography sx={{ fontWeight: 700, color: IPD_COLORS.primary }}>Grand Total</Typography>
                    <Typography sx={{ fontWeight: 700, color: IPD_COLORS.primary, fontFamily: 'monospace' }}>
                      {formatCurrency(financials.grandTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {validationError && lines.length > 0 && (
              <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 13, mt: 2, textAlign: 'right' }}>
                {validationError}
              </Typography>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <InvButton loading={saving} disabled={Boolean(validationError)} onClick={handleSubmit}>
                Create Purchase Invoice
              </InvButton>
            </Box>
          </SectionCard>
        </>
      )}
    </Box>
  );
}
